import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api, mediaUrl } from '@/lib/api';
import { ArrowRight, Sparkles } from 'lucide-react';
import { sanitizeHtml } from '@/lib/sanitize';

export default function MeetGPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/meet-g').then(({ data }) => setData(data)).catch(() => setData({})); }, []);
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[var(--pg-ink)]">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16" data-testid="meet-g-page">
      {/* Hero */}
      <section className="pg-card overflow-hidden mb-10">
        {data.hero_url ? (
          data.hero_kind === 'video' ? (
            <video src={mediaUrl(data.hero_url)} controls className="w-full max-h-[60vh] bg-black" />
          ) : (
            <img src={mediaUrl(data.hero_url)} alt="Meet G" className="w-full max-h-[60vh] object-cover" />
          )
        ) : (
          <div className="aspect-[16/9] flex items-center justify-center text-[var(--pg-ink-faint)] font-mono tracking-widest text-xs">HERO MEDIA PENDING</div>
        )}
      </section>

      <motion.div initial={{ opacity: 1, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
          <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em] uppercase" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)' }}>MEET G</p>
        </span>
        <div className="mt-2">
          <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-3 py-1.5">
            <h1 className="font-display text-4xl sm:text-5xl text-[var(--pg-ink)] tracking-[0.04em]" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)' }}>THE CURATOR</h1>
          </span>
        </div>
      </motion.div>

      <Section title="BIOGRAPHY" body={data.biography} testId="meet-g-bio" />
      <Section title="MISSION" body={data.mission} testId="meet-g-mission" />
      <Section title="CURRENT FOCUS" body={data.current_focus} testId="meet-g-focus" />

      {Array.isArray(data.gallery_urls) && data.gallery_urls.length > 0 && (
        <section className="mt-12" data-testid="meet-g-gallery">
          <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
            <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em]" style={{ textShadow: '0 2px 14px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)' }}>GALLERY</p>
          </span>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4">
            {data.gallery_urls.map((u, i) => (
              <div key={i} className="pg-card overflow-hidden aspect-square">
                <img src={mediaUrl(u)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="mt-16 sm:mt-20 text-center">
        <Link to="/collab" data-testid="meet-g-collab-cta" className="pg-btn-primary inline-flex items-center gap-2 !px-8 !py-4 text-sm">
          <Sparkles size={14} /> COLLAB WITH G <ArrowRight size={14} />
        </Link>
        <p className="text-[10px] font-mono tracking-[0.32em] text-[var(--pg-ink-faint)] mt-3 uppercase">Open the room</p>
      </section>
    </div>
  );
}

function Section({ title, body, testId }) {
  if (!body) return null;
  const trimmed = String(body).trim();
  const isHtml = /^<(p|h[1-6]|ul|ol|blockquote|pre|hr|div)(\s|>)/i.test(trimmed);
  return (
    <section className="mt-10 bg-[rgba(10,8,16,0.55)] rounded-xl p-5 sm:p-6" data-testid={testId}>
      <p className="pg-text-glow-gold font-mono text-[10px] tracking-[0.32em]" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)" }}>{title}</p>
      <div className="pg-prose mt-3 text-[var(--pg-ink)]" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.85)" }}>
        {isHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(trimmed) }} />
        ) : (
          String(body).split(/\n\n+/).map((para, i) => (
            <p key={i} className="text-[var(--pg-ink)]">{para}</p>
          ))
        )}
      </div>
    </section>
  );
}
